import React from 'react';
import moment from 'moment';
import cx from 'classnames';
import { CountryCode } from 'libphonenumber-js';

import { capitalizeAvatarName, concatFullName } from 'utils/common';
import {
  ContactUser,
  FamilyTimeSlot,
  SelectedPatient,
} from 'interfaces/familyBookingTypes';
import {
  formatPhoneNumberWithCountryCode,
  geCountryByPhoneNumber,
} from 'utils/formatPhoneNumber';
import { useAppSelector } from 'redux/reduxHooks';

import styles from './PatientCard.module.scss';

interface PatientCardProps {
  patient: ContactUser & Partial<SelectedPatient>;
  isPointOfContact: boolean;
  colorIndex: number;
  isBookable?: boolean;
}

interface IPatientInfoField {
  label: string;
  property: string;
}

const INFO_FIELDS: IPatientInfoField[] = [
  { label: 'Date of birth', property: 'dob' },
  { label: 'Email', property: 'email' },
  { label: 'Phone number', property: 'phoneNumber' },
  { label: 'Home address', property: 'homeAddress' },
  { label: 'Date & time', property: 'appointmentDateTime' },
  { label: 'Reason', property: 'reason' },
  { label: 'Practitioner', property: 'practitioner' },
  { label: 'Last cleaning', property: 'lastHygieneAppt' },
];

const PatientCard = (props: PatientCardProps) => {
  const {
    familyBookingSlice: { slotsPerDate },
    clinicDetailsSlice: {
      clinicData: { services },
    },
  } = useAppSelector((state) => state);

  const { patient, isPointOfContact, colorIndex, isBookable = true } = props;

  const avatarText = capitalizeAvatarName(
    `${patient.firstName.replace(/\s/g, '').trim()} ${patient.lastName
      .replace(/\s/g, '')
      .trim()}`
  );

  const hasAppointmentDateTime =
    !!patient.appointmentDate && !!patient.startTime;

  const hasPractitioner = !!patient.appointmentDate && !!patient.doctorId;

  const resolveValue = (property: string) => {
    if (property === 'dob') {
      return moment(patient.dob, 'YYYY-MM-DD').format('MMM DD, YYYY');
    }

    if (property === 'phoneNumber') {
      if (!patient.phoneNumber) return null;

      const countryCode = geCountryByPhoneNumber(patient.phoneNumber);

      return countryCode
        ? formatPhoneNumberWithCountryCode(
            patient.phoneNumber,
            countryCode as CountryCode
          )
        : patient.phoneNumber;
    }

    if (property === 'email') {
      return patient.email || null;
    }

    if (property === 'homeAddress') {
      return patient.homeAddress?.trim() || null;
    }

    if (property === 'lastHygieneAppt') {
      if (!patient.lastHygieneAppt?.startDate) return null;

      return moment(patient.lastHygieneAppt.startDate, 'YYYY-MM-DD').format(
        'MMM DD, YYYY'
      );
    }

    if (property === 'reason' && patient.serviceId) {
      return services.find((svc) => svc.id === patient.serviceId)?.name || null;
    }

    if (property === 'appointmentDateTime' && hasAppointmentDateTime) {
      const apptDate = moment(patient.appointmentDate, 'YYYY-MM-DD').format(
        'MMM DD, YYYY'
      );

      const apptStartTime = moment(patient.startTime, 'HH:mm:ss').format(
        'hh:mm A'
      );

      return `${apptDate} - ${apptStartTime}`;
    }

    if (property === 'practitioner' && hasPractitioner) {
      let doctorName = patient.practitionerName || null;

      if (!doctorName && slotsPerDate !== null) {
        const slots = slotsPerDate[patient.appointmentDate as string];

        doctorName =
          slots?.find(
            (slot: FamilyTimeSlot) => slot.doctor.id === patient.doctorId
          )?.doctor?.name || null;
      }

      return doctorName;
    }

    return null;
  };

  const infoFields = INFO_FIELDS.map((field) => ({
    label: field.label,
    value: resolveValue(field.property),
  })).filter((field) => field.value !== null);

  const avatarClassName = cx(
    styles['avatar'],
    styles[`avatar-color-${colorIndex}`]
  );

  return (
    <div className={styles['container']}>
      <div className={styles['left-section']}>
        {isPointOfContact && (
          <div className={styles['point-of-contact']}>POINT OF CONTACT</div>
        )}

        <div className={styles['identity']}>
          <div className={avatarClassName}>{avatarText}</div>

          <div className={styles['full-name']}>
            {concatFullName(patient.firstName, patient.lastName)}
          </div>
        </div>
      </div>

      <div className={styles['right-section']}>
        {!isBookable && (
          <div className={styles['incomplete-appt']}>
            INCOMPLETE APPOINTMENT
          </div>
        )}

        {infoFields.map((field) => (
          <div key={field.label} className={styles['user-info']}>
            <span className={styles['user-detail']}>{field.label}:</span>

            <span className={styles['user-data']}>{field.value}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default PatientCard;
